import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {App as AntdApp, Table, Tag, Modal, Input, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading, PageHeader} from "~/components";
import {useDebounce} from "~/hooks";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetSavedSchemasQuery,
	useGetSavedSchemaQuery,
	useDeleteSavedSchemaMutation,
} from "~/reduxs/api/schemaApiSlice";
import JsonLdOutput from "../components/JsonLdOutput";
import style from "../style/SchemaTool.module.scss";

const cn = className.bind(style);

/** Parse json_ld string đã lưu → object (an toàn). */
const parseJson = (str) => {
	try { return JSON.parse(str); } catch (e) { return null; }
};

function SchemaSaved() {

	const navigate = useNavigate();
	const {notification, modal} = AntdApp.useApp();

	const [keyword, setKeyword] = useState('');
	const search = useDebounce(keyword.trim());
	const {data: rows = [], isLoading, isFetching} = useGetSavedSchemasQuery(search);
	const [deleteSavedSchema] = useDeleteSavedSchemaMutation();

	const [viewId, setViewId] = useState(null);
	const {data: detail, isFetching: loadingDetail} = useGetSavedSchemaQuery(viewId, {skip: !viewId});

	const handleDelete = (row) => {
		modal.confirm({
			title: 'Xóa schema',
			content: `Xóa schema "${row.label}"?`,
			okText: 'Xóa',
			okButtonProps: {danger: true},
			cancelText: 'Hủy',
			onOk: async () => {
				try {
					await deleteSavedSchema(row.id).unwrap();
					notification.success({message: 'Thành công', description: 'Đã xóa schema'});
				} catch (e) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Xóa thất bại')});
				}
			},
		});
	};

	const columns = [
		{title: 'Tên', dataIndex: 'label', key: 'label'},
		{title: 'Loại', dataIndex: 'type', key: 'type', width: 200, render: (t) => <Tag color="blue">{t}</Tag>},
		{title: 'Thời gian', dataIndex: 'created', key: 'created', width: 170},
		{
			title: '', key: 'actions', width: 160,
			render: (_, r) => (
				<div className={cn('table-actions')}>
					<Tooltip title="Xem JSON-LD">
						<Button small blue onClick={() => setViewId(r.id)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-eye" />} />
					</Tooltip>
					<Tooltip title="Sửa (mở lại form Generator)">
						<Button small outline onClick={() => navigate('/schema/generator', {state: {editId: r.id}})}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />} />
					</Tooltip>
					<Tooltip title="Xoá">
						<Button small red onClick={() => handleDelete(r)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
					</Tooltip>
				</div>
			),
		},
	];

	if (isLoading) return <Loading />;

	const detailJson = detail ? parseJson(detail.json_ld) : null;

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-bookmark"
				title="Schema đã lưu"
				subtitle="Các schema JSON-LD bạn đã tạo"
				actions={
					<Button to="/schema/generator" primary
						leftIcon={<FontAwesomeIcon icon="fa-light fa-shapes" />}>
						Tạo mới
					</Button>
				}
			/>

			<Input
				allowClear
				prefix={<FontAwesomeIcon icon="fa-light fa-magnifying-glass" />}
				placeholder="Tìm theo tên hoặc loại schema…"
				value={keyword}
				onChange={(e) => setKeyword(e.target.value)}
				style={{maxWidth: 320, marginBottom: 14}}
			/>

			<Table
				rowKey="id"
				columns={columns}
				dataSource={rows}
				loading={isFetching}
				locale={{emptyText: search ? `Không có schema nào khớp “${search}”` : 'Chưa có schema nào'}}
				pagination={{pageSize: 10, hideOnSinglePage: true}}
			/>

			<Modal
				open={!!viewId}
				onCancel={() => setViewId(null)}
				footer={null}
				width={760}
				title={detail ? detail.label : 'Schema'}
			>
				{loadingDetail ? <Loading noFixed /> : (detailJson && <JsonLdOutput json={detailJson} />)}
			</Modal>
		</div>
	);
}

export default SchemaSaved;
